/* eslint-disable react-hooks/exhaustive-deps */
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import {
  PhoneNumber,
  TextInput,
} from '@theguarantors/ui-kit-v3';
import { withMessage } from '@theguarantors/ui-kit-v3/decorators/withMessage';
import Sidebar from 'src/components/Sidebar/Sidebar';
import ApplicationSchema from 'src/configs/APISchemas/Application.schema';
import { CREATE_APPLICATION } from 'src/configs/APISchemas/CreateApplication';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import {
  UpdateApplication,
  UpdateApplicationRoute,
} from 'src/redux/actions/Application';
import { Store } from '../../types/Store.types';
import { Divider, Grid, Button, Typography, Box, CircularProgress } from '@mui/material';
import styles from './OrganisationStartApplication.module.scss';
import { Dropdown } from '../FormComponents/Components/Dropdown';


export const StyledPhoneNumber = styled(PhoneNumber as any)`
  max-width: none;
`;

export const StyledDropdown = styled(Dropdown as any)`
> div:nth-child(2) {
  height: 3.5rem;
}
> div:nth-child(3) div[tabIndex="-1"] {
  padding: 1rem 1rem;
}
`;
export const OrganisationStartApplication = ({
  addresses,
  organisation,
  logo,
}: {
  addresses: any[];
  organisation: string;
  logo: JSX.Element | undefined;
}) => {
  type HomeParams = {
    name: string;
  };
  const params: any = useParams<HomeParams>();
  const application = useSelector((state: Store) => state.application);
  const [loading, setLoading] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const formHook = useForm<ApplicationSchema>({
    defaultValues: application.data,
    reValidateMode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setFocus,
    formState: { errors },
  } = formHook;
  useEffect(() => {
    setFocus('firstName');
    if (
      application.data.firstName &&
      application.data.lastName &&
      application.data.email &&
      application.data.phone &&
      application.data.buildingAddress &&
      application.data.buildingUnit
    ) {
      setIsValid(true);
    }
    watch((formValues: any) => {
      if (formValues) {
        if (
          formValues.firstName &&
          formValues.lastName &&
          formValues.email &&
          formValues.phone &&
          formValues.buildingAddress &&
          formValues.buildingUnit
        ) {
          setIsValid(true);
        } else {
          setIsValid(false);
        }
        dispatch(
          UpdateApplication({
            ...formValues,
            COMPANY_VENDOR: organisation,
          }),
        );
        if (formValues.email && !formValues.phone) {
          setFocus('phone');
        }
      }
    });
    if (params && params.name) {
      const name = params.name.replace(/[- _]/g, ' ');
      const selectedAddress = addresses.find(
        (add) =>
          add.name.replace(/[- _]/g, ' ').toLowerCase() === name.toLowerCase(),
      );
      if (selectedAddress) {
        addressSelection({ value: { value: selectedAddress } });
      }
    }
  }, []);

  const onSubmit = async (values: ApplicationSchema) => {
    try {
      setLoading(true);
      dispatch(UpdateApplication(values));
      const mutation = CREATE_APPLICATION(values);
      const response = await GraphQLRequest(mutation);
      setLoading(false);
      if (response.createApplication) {
        if (response.createApplication.errors) {
          return toast(response.createApplication.errors[0].message, {
            type: 'error',
          });
        } else {
          dispatch(
            UpdateApplication({
              ...application.data,
              ...response.createApplication,
              COMPANY_VENDOR: organisation,
            }),
          );
          dispatch(UpdateApplicationRoute('/quote-information'));
          history.push('/quote-information');
          return;
        }
      }
      throw Error('Application Not Created');
    } catch (error: any) {
      console.error(error);
      setLoading(false);
      if (error?.response?.data?.errors) {
        return toast(error.response.data.errors[0].message, {
          type: 'error',
        });
      }
      toast('We are having some trouble, Please contact support for help.', {
        type: 'error',
      });
    }
  };
  const addressSelection = (value: any) => {
    setValue('buildingAddress', {
      label: value.label,
      value: value.value,
    });
    setValue('buildingName', value.value.name);
    setValue('buildingId', value.value.id);
    setValue('address.street', value.value.street);
    setValue('address.municipality', value.value.municipality);
    setValue('address.postalCode', value.value.postalCode);
    setValue('address.state', value.value.state);
    setFocus('buildingUnit');
  };
  return (
    <Box className="mx-5 mt-5">
      <Grid container spacing={1} className={`mt-md-5 pt-md-5 ${styles.layout}`}>
        <Grid item xs={12} lg={8.5}>
          <Button
            variant="text"
            startIcon={<ArrowBackIosIcon />}
            onClick={() => history.goBack()}
          >
            Back
          </Button>
          <Typography variant='h2'>Let’s get you started!</Typography>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-100 mt-4"
          >
            <Grid container flexDirection={{ xs: 'column', md: 'row' }} spacing={3}>
              <Grid item xs={12} lg={2.5}>
                <Typography variant='h6'>
                  Tell us about yourself.
                </Typography>
              </Grid>
              <Grid item xs={12} lg={7}>
                <Grid container spacing={2} columnSpacing={4}>
                  <Grid item xs={6}>
                    <Controller
                      control={control}
                      name={'firstName'}
                      rules={{
                        required: true,
                        maxLength: 50,
                      }}
                      render={({ field: { ref, ...field } }) =>
                        withMessage(
                          <TextInput
                            required
                            label="First Name"
                            controlled={false}
                            pattern="[\w -]*"
                            {...field}
                            setInputRef={ref}
                          ></TextInput>,
                          {
                            message: errors?.firstName,
                            type: 'error',
                          },
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      control={control}
                      name={'lastName'}
                      rules={{
                        required: true,
                        maxLength: 50,
                      }}
                      render={({ field: { ref, ...field } }) =>
                        withMessage(
                          <TextInput
                            required
                            label="Last Name"
                            controlled={false}
                            pattern="[\w -]*"
                            {...field}
                            setInputRef={ref}
                          ></TextInput>,
                          {
                            message: errors?.lastName,
                            type: 'error',
                          },
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={'email'}
                      rules={{
                        required: true,
                        maxLength: 70,
                      }}
                      render={({ field: { ref, ...field } }) =>
                        withMessage(
                          <TextInput
                            label="Email"
                            type={'email'}
                            controlled={false}
                            pattern="^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$"
                            maxLength={70}
                            required
                            {...field}
                            setInputRef={ref}
                          ></TextInput>,
                          {
                            message: errors?.email,
                            type: 'error',
                          },
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={'phone'}
                      rules={{
                        required: true,
                      }}
                      render={({ field: { onChange, ...fields } }) =>
                        withMessage(
                          <StyledPhoneNumber
                            {...fields}
                            required
                            label="Phone Number"
                            onChange={onChange}
                            isAllowed={(value: { value: string }) => {
                              if (
                                !value.value ||
                                /^[2-9]/.test(value.value)
                              ) {
                                return true;
                              }
                              return false;
                            }}
                          />,
                          {
                            message: errors?.phone,
                            type: 'error',
                          },
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container flexDirection={{ xs: 'column', md: 'row' }} spacing={3} marginTop={1}>
              <Grid item xs={12} lg={2.5}>
                <Typography variant='h6'>
                  Where is the address that you are renting?
                </Typography>
                <Typography variant='subtitle1'>
                  This is the address that will be used for your renter’s
                  insurance policy.
                </Typography>
              </Grid>
              <Grid item xs={12} lg={7}>
                <Grid container spacing={2} columnSpacing={4}>
                  <Grid item xs={12}>
                    {withMessage(
                      <StyledDropdown
                        options={addresses.map((add) => ({
                          label: add.complete_address,
                          value: add,
                          key: add.complete_address,
                        }))}
                        name={'buildingAddress'}
                        isSearchable={true}
                        value={getValues().buildingAddress}
                        onChange={addressSelection}
                        isClearable={false}
                        placeholder="Search Property"
                      />,
                      {
                        message: errors.buildingAddress,
                        type: 'error',
                      },
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={'buildingUnit'}
                      rules={{
                        required: false,
                      }}
                      render={({
                        field: { onChange, onBlur, value, name, ref },
                      }) => (
                        <TextInput
                          controlled={false}
                          label="Unit Number"
                          maxLength={50}
                          name={name}
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                          setInputRef={ref}
                        ></TextInput>
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container className='my-4'>
              <Grid item xs={12} lg={7.5} display={{ xs: 'none', lg: 'block' }}></Grid>
              <Grid item xs={12} lg={2}>
                <Button
                  variant='contained'
                  disabled={!isValid}
                  type="submit"
                  fullWidth={true}
                  startIcon={loading ? <CircularProgress color="inherit" size="17px"/> : ''}
                >
                  Continue <ArrowForwardIosIcon sx={{height:"20px"}}/>
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
        <Divider orientation="vertical" flexItem />
        <Grid item lg={3} display={{ xs: "none", md: "block" }}>
          <Sidebar logo={logo} />
        </Grid>
      </Grid >
    </Box >
  );
};
